// src/Pages/Hmo/ApplyForPlan.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ApplyForPlan() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { linkId } = useParams();
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [linkIdFromQuery, setLinkIdFromQuery] = useState(null);

  // ✅ Brand Color
  const BRAND_COLOR = '#F89423';

  // ✅ Get params safely
  useEffect(() => {
    try {
      const company = searchParams.get('company') || linkId;
      const link = searchParams.get('link') || linkId;
      setCompanyId(company);
      setLinkIdFromQuery(link);
      console.log('📍 Route Params:', { company, link, linkId });
    } catch (err) {
      console.error('Error getting params:', err);
      toast.error('Invalid link parameters');
    }
  }, [searchParams, linkId]);

  const options = [
    {
      id: 'buy',
      title: 'Buy Hmo',
      description: 'Get Hmo for yourself and family in few clicks',
      icon: 'fa-tag',
      color: '#F89423'  // ✅ Orange
    },
    {
      id: 'renew',
      title: 'Renew Hmo',
      description: 'Seamlessly renew your Hmo with Fsl',
      icon: 'fa-sync-alt',
      color: '#F89423'  // ✅ Orange
    },
    
  ];

  const handleContinue = () => {
    if (!selectedOption) {
      toast.warning('Please select an option to continue');
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      switch(selectedOption) {
        case 'buy':
          navigate(`/insurance/buy?company=${companyId}&link=${linkIdFromQuery}`);
          break;
        case 'renew':
          navigate(`/insurance/renew?company=${companyId}&link=${linkIdFromQuery}`);
          break;
        case 'manage':
          navigate(`/insurance/manage?company=${companyId}&link=${linkIdFromQuery}`);
          break;
        default:
          setLoading(false);
      }
    }, 500);
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f3f4f6' }}>
      <ToastContainer position="top-center" autoClose={3000} />
      
      {/* Header */}
      <header className="bg-white border-bottom py-3">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            {/* ✅ FSL Logo */}
            <div className="d-flex align-items-center">
              <img 
                 src="/images/fsl_logo.png"
                alt="FSL Logo" 
                style={{ height: '50px' }} 
              />
            </div>
            
            <button 
              onClick={() => navigate('/')}
              className="btn btn-light rounded-circle"
              style={{ width: '36px', height: '36px' }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            {/* Title */}
            <div className="text-center mb-5">
              <h1 className="fw-bold mb-3" style={{ color: '#111827' }}>
                Hmo by FSL
              </h1>
              <p className="text-muted">
                Link ID: <strong>{linkIdFromQuery || linkId || 'N/A'}</strong>
              </p>
            </div>

            {/* Options */}
            <div className="d-flex flex-column gap-3 mb-5">
              {options.map((option) => (
                <div
                  key={option.id}
                  onClick={() => setSelectedOption(option.id)}
                  className="p-4 rounded-3 bg-white"
                  style={{
                    border: `2px solid ${selectedOption === option.id ? BRAND_COLOR : '#e5e7eb'}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: selectedOption === option.id ? '0 4px 6px -1px rgba(248, 148, 35, 0.2)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedOption !== option.id) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedOption !== option.id) {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }
                  }}
                >
                  <div className="d-flex align-items-start gap-3">
                    <div 
                      className="rounded-3 p-3"
                      style={{ 
                        backgroundColor: `${BRAND_COLOR}15`, 
                        color: BRAND_COLOR 
                      }}
                    >
                      <i className={`fas ${option.icon}`} style={{ fontSize: '24px' }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <h3 className="fw-semibold mb-1" style={{ color: '#111827' }}>
                        {option.title}
                      </h3>
                      <p className="mb-0 text-muted">{option.description}</p>
                    </div>
                    {selectedOption === option.id && (
                      <div className="flex-shrink-0">
                        <i 
                          className="fas fa-check-circle" 
                          style={{ fontSize: '24px', color: BRAND_COLOR }}
                        ></i>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Continue Button */}
            <button
              onClick={handleContinue}
              disabled={loading || !selectedOption}
              className="btn w-100 py-3 fw-semibold rounded-3"
              style={{
                backgroundColor: selectedOption ? BRAND_COLOR : '#9ca3af',
                color: 'white',
                border: 'none',
                transition: 'all 0.3s ease',
                opacity: selectedOption ? 1 : 0.6
              }}
              onMouseEnter={(e) => {
                if (selectedOption) {
                  e.target.style.backgroundColor = '#e0851f';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedOption) {
                  e.target.style.backgroundColor = BRAND_COLOR;
                }
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : (
                'Continue'
              )}
            </button>

            {/* Footer Info */}
            <div className="text-center mt-4">
              <p className="text-muted small mb-0">
                Secure Hmo services powered by FSL
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default ApplyForPlan;